import { useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Box, Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';

type UsageReportCardProps = {
    title: string;
    description: string;
    month: Dayjs | null;
    onMonthChange: (value: Dayjs | null) => void;
    action: string;
    loading: boolean;
    onLoadingChange: (value: boolean) => void;
    error: string | null;
    onErrorChange: (value: string | null) => void;
};

function UsageReportCard({ title, description, month, onMonthChange, action, loading, onLoadingChange, error, onErrorChange }: UsageReportCardProps) {
    const canDownload = useMemo(() => !!month, [month]);

    const getFilename = (response: Response) => {
        const contentDisposition = response.headers.get('content-disposition');
        if (!contentDisposition) {
            return `${action}.csv`;
        }

        const filenameStar = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
        if (filenameStar?.[1]) {
            try {
                return decodeURIComponent(filenameStar[1]);
            } catch {
                return filenameStar[1];
            }
        }

        const filename = contentDisposition.match(/filename\s*=\s*("?)([^";]+)\1/i);
        return filename?.[2] ?? `${action}.csv`;
    };

    const onDownload = async () => {
        if (!month) {
            return;
        }

        onLoadingChange(true);
        onErrorChange(null);

        try {
            const query = new URLSearchParams({
                month: month.startOf('month').format('YYYY-MM-DD')
            });

            const response = await fetch(`api/UsageReports/${action}?${query.toString()}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                const message = await response.text();
                throw new Error(message || 'Unable to download report.');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getFilename(response);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
        } catch (downloadError) {
            onErrorChange(downloadError instanceof Error ? downloadError.message : 'Unable to download report.');
        } finally {
            onLoadingChange(false);
        }
    };

    return (
        <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {description}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                    label="Month"
                    value={month}
                    onChange={(value) => onMonthChange(value?.startOf('month') ?? null)}
                    views={['year', 'month']}
                    openTo="month"
                    minDate={dayjs().startOf('month').subtract(12, 'month')}
                    maxDate={dayjs().startOf('month').subtract(1, 'month')}
                    slotProps={{ textField: { fullWidth: true } }}
                />
                <Button variant="contained" onClick={() => void onDownload()} disabled={!canDownload || loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {loading ? 'Downloading...' : 'Download CSV'}
                </Button>
                {error && (
                    <Alert severity="error" variant="outlined">
                        {error}
                    </Alert>
                )}
            </Box>
        </Paper>
    );
}

export default function UsageReportsPage() {
    const defaultMonth = dayjs().startOf('month').subtract(1, 'month');
    const [tripRateMonth, setTripRateMonth] = useState<Dayjs | null>(defaultMonth);
    const [loginMonth, setLoginMonth] = useState<Dayjs | null>(defaultMonth);
    const [tripRateLoading, setTripRateLoading] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);
    const [tripRateError, setTripRateError] = useState<string | null>(null);
    const [loginError, setLoginError] = useState<string | null>(null);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h4" gutterBottom>
                        Usage Reports
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Export monthly usage summaries for trip rate calculations and logins.
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <UsageReportCard
                        title="Trip Rate Calculations"
                        description="Trip rate calculations by Main and Sub land use. This will take several minutes to complete."
                        month={tripRateMonth}
                        onMonthChange={setTripRateMonth}
                        action="ExportTripRateCsv"
                        loading={tripRateLoading}
                        onLoadingChange={setTripRateLoading}
                        error={tripRateError}
                        onErrorChange={setTripRateError}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <UsageReportCard
                        title="Logins by Day"
                        description="Login counts by day of the month."
                        month={loginMonth}
                        onMonthChange={setLoginMonth}
                        action="ExportLoginsCsv"
                        loading={loginLoading}
                        onLoadingChange={setLoginLoading}
                        error={loginError}
                        onErrorChange={setLoginError}
                    />
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
}
